import "./styles.css";
import {
  initAuth,
  isConfigured,
  getAccount,
  login,
  logout,
} from "./auth.js";
import {
  ensurePaperReaderFolder,
  getResolvedFolder,
  loadLibrary,
  saveLibrary,
  loadResearchNotes,
  saveResearchNotes,
  loadPaperNotebook,
  savePaperNotebook,
  readTextFile,
  whoAmI,
  diagnoseCloud,
  localDataAvailable,
  syncLocalFilesToCloud,
  readJsonFile,
} from "./graph.js";
import { toPaperReaderRelative, ozetPath } from "./paths.js";
import { config } from "./config.js";

const SECTIONS = [
  ["methods", "Methods"],
  ["ideas", "Ideas"],
  ["hypotheses", "Hypotheses"],
  ["literature", "Literature"],
  ["questions", "Questions"],
  ["experiments", "Experiments"],
  ["general", "General"],
];

const state = {
  tab: "library",
  view: "list", // list | detail | edit | summary | setup
  library: [],
  research: [],
  notes: [],
  selected: null,
  summaryText: "",
  busy: false,
  error: "",
  toast: "",
  accountInfo: null,
  dataFolder: "",
  localMode: false,
  showLocalButton: false,
};

const app = document.getElementById("app");

function uid() {
  return Math.random().toString(16).slice(2, 14);
}

function nowIso() {
  return new Date().toISOString().slice(0, 19) + "Z";
}

function toast(msg) {
  state.toast = msg;
  render();
  setTimeout(() => {
    if (state.toast === msg) {
      state.toast = "";
      render();
    }
  }, 2400);
}

function setError(err) {
  state.error = err ? String(err.message || err) : "";
}

async function withBusy(fn) {
  state.busy = true;
  setError("");
  render();
  try {
    await fn();
  } catch (err) {
    setError(err);
  } finally {
    state.busy = false;
    render();
  }
}

async function refreshAll() {
  const info = await whoAmI();
  state.accountInfo = info;
  state.localMode = false;

  // If Mac Vite can see local files and cloud is empty, push once to OneDrive.
  if (state.showLocalButton && getAccount()) {
    try {
      await ensurePaperReaderFolder().catch(() => null);
      const cloud = await readJsonFile("my_library.json");
      const cloudCount = Array.isArray(cloud?.papers) ? cloud.papers.length : 0;
      const localDiag = await (await fetch("/local-diagnose")).json();
      if ((localDiag.papers || 0) > cloudCount) {
        toast("OneDrive buluta senkronize ediliyor…");
        await syncLocalFilesToCloud();
        toast("Buluta yüklendi");
      }
    } catch (err) {
      console.warn("cloud sync", err);
    }
  }

  const ensured = await ensurePaperReaderFolder();
  state.dataFolder = ensured.folder || getResolvedFolder();
  const [library, research, notes] = await Promise.all([
    // Prefer Graph (canonical for phone); local fetch only fills if Graph empty during sync race
    (async () => {
      const fromCloud = await readJsonFile("my_library.json");
      const papers = Array.isArray(fromCloud?.papers) ? fromCloud.papers : [];
      if (papers.length) return papers;
      return loadLibrary();
    })(),
    loadResearchNotes(),
    loadPaperNotebook(),
  ]);
  state.library = library;
  state.research = research;
  state.notes = notes;
  if (!library.length) {
    const email =
      info?.mail || info?.userPrincipalName || getAccount()?.username || "?";
    setError(
      `Library boş.\nHesap: ${email}\nKlasör: ${state.dataFolder}\n\n` +
        `Bu Mac’te bir kez Microsoft ile giriş yapıp bekleyin (otomatik bulut yüklemesi).\n` +
        `Sonra telefondan aynı hesapla girin.`
    );
  } else {
    setError("");
  }
}

function topbar(titleRight = "") {
  const account = getAccount();
  const email =
    state.accountInfo?.mail ||
    state.accountInfo?.userPrincipalName ||
    account?.username ||
    "";
  const folder = state.dataFolder || config.paperReaderFolder;
  return `
    <header class="topbar">
      <div>
        <div class="brand">Paper Reader</div>
        <div class="muted">${escapeHtml(titleRight || email || "OneDrive")}</div>
        ${email ? `<div class="muted" style="font-size:0.8rem">${escapeHtml(folder)} · ${state.library.length} paper</div>` : ""}
      </div>
      <div class="row">
        ${
          account
            ? `<button class="btn btn-ghost" data-action="logout">Çıkış</button>`
            : ""
        }
      </div>
    </header>
  `;
}

function nav() {
  const tabs = [
    ["library", "Library"],
    ["research", "Research"],
    ["notes", "Notes"],
  ];
  return `
    <nav class="nav">
      ${tabs
        .map(
          ([id, label]) =>
            `<button data-tab="${id}" class="${state.tab === id ? "active" : ""}">${label}</button>`
        )
        .join("")}
    </nav>
  `;
}

function setupScreen() {
  return `
    <section class="hero setup">
      <h1>Paper Reader Mobile</h1>
      <p>
        Aynı OneDrive library ve notebook’a telefondan eriş. Mac/PC kapalı olsa da
        Microsoft hesabınla giriş yeterli.
      </p>
      <div class="card stack">
        <strong>Bir kerelik Azure ayarı (ücretsiz)</strong>
        <ol>
          <li>https://portal.azure.com — <em>kişisel</em> Microsoft hesabı ile gir (iş hesabı değil)</li>
          <li>«tenant Microsoft Services» hatası alırsan: https://azure.microsoft.com/free/ ile ücretsiz abonelik aç, sonra tekrar dene</li>
          <li>App registrations → New → <strong>Personal Microsoft accounts only</strong></li>
          <li>Redirect URI (SPA): bu sitenin URL’si (veya <code>http://localhost:5173/</code>)</li>
          <li>API permissions: <code>User.Read</code>, <code>Files.ReadWrite</code></li>
          <li>Application (client) ID → <code>mobile-web/src/config.js</code></li>
        </ol>
        <p class="muted">Detay: <code>mobile-web/README.md</code>. Azure Portal ID’sini (<code>c44b4083-…</code>) clientId olarak kullanma.</p>
      </div>
    </section>
  `;
}

function loginScreen() {
  return `
    ${topbar("Giriş gerekli")}
    <section class="hero">
      <h1>Paper Reader</h1>
      <p>
        Telefondan ve bilgisayardan aynı OneDrive library / notebook.
        Mac açık veya kapalı fark etmez — Microsoft hesabınla gir.
      </p>
      <div class="card" style="margin-bottom:16px">
        <p class="muted" style="margin:0">
          OneDrive – Personal hesabını kullan (masaüstündeki Paper Reader’ın yazdığı hesap).
          Klasör: <code>${config.paperReaderFolder}</code>
        </p>
      </div>
      <button class="btn btn-primary" data-action="login">Microsoft ile giriş</button>
    </section>
  `;
}

function libraryList() {
  if (!state.library.length) {
    return `
      <div class="empty">
        Library boş.<br/>
        <span class="muted">Hesap / klasör üstteki kırmızı kutuda.</span>
        <div class="row" style="justify-content:center;margin-top:14px">
          <button class="btn btn-accent" data-action="reload">Yeniden yükle</button>
          <button class="btn" data-action="logout">Çıkış / başka hesap</button>
        </div>
      </div>`;
  }
  return `
    <div class="list">
      ${state.library
        .map((p, i) => {
          const status = p.status === "summarized" ? "özetlendi" : "bekliyor";
          const badge = p.status === "summarized" ? "ok" : "warn";
          return `
            <button class="item" data-open-library="${i}">
              <h3>${escapeHtml(p.title || "Untitled")}</h3>
              <div class="meta">
                <span class="badge ${badge}">${status}</span>
                <span>${escapeHtml(p.year || "")}</span>
                <span>${escapeHtml(truncate(p.authors || "", 42))}</span>
              </div>
              ${
                p.quick_note
                  ? `<p class="muted" style="margin:8px 0 0">${escapeHtml(truncate(p.quick_note, 110))}</p>`
                  : ""
              }
            </button>
          `;
        })
        .join("")}
    </div>
  `;
}

function libraryDetail(p) {
  return `
    <div class="detail stack">
      <button class="btn btn-ghost" data-action="back">← Liste</button>
      <h2>${escapeHtml(p.title || "Untitled")}</h2>
      <div class="meta">
        <span class="badge ${p.status === "summarized" ? "ok" : "warn"}">${escapeHtml(p.status || "")}</span>
        <span>${escapeHtml(p.year || "")}</span>
        <span>${escapeHtml(p.journal || "")}</span>
      </div>
      <p class="muted">${escapeHtml(p.authors || "")}</p>
      ${p.url ? `<a class="btn" href="${escapeAttr(p.url)}" target="_blank" rel="noopener">Makaleyi aç</a>` : ""}
      <div class="card">
        <strong>Abstract</strong>
        <div class="prose" style="margin-top:8px">${escapeHtml(p.abstract || "—")}</div>
      </div>
      <div class="field">
        <label>Quick note</label>
        <textarea id="quick-note">${escapeHtml(p.quick_note || "")}</textarea>
      </div>
      <div class="row">
        <button class="btn btn-accent" data-action="save-quick-note">Notu kaydet</button>
        ${
          p.summary_folder
            ? `<button class="btn btn-primary" data-action="open-summary">Özeti oku</button>`
            : ""
        }
      </div>
    </div>
  `;
}

function researchList() {
  return `
    <div class="stack">
      <div class="row" style="justify-content:space-between">
        <strong>Research notebook</strong>
        <button class="btn btn-primary" data-action="new-research">+ Not</button>
      </div>
      ${
        state.research.length
          ? `<div class="list">${state.research
              .map((n, i) => {
                const label = SECTIONS.find(([id]) => id === n.section)?.[1] || n.section;
                return `
                  <button class="item" data-open-research="${i}">
                    <div class="meta"><span class="badge">${escapeHtml(label)}</span><span>${escapeHtml((n.updated_at || "").slice(0, 10))}</span></div>
                    <h3>${escapeHtml(n.title || "Untitled")}</h3>
                    <p class="muted" style="margin:6px 0 0">${escapeHtml(truncate(n.content || "", 120))}</p>
                  </button>`;
              })
              .join("")}</div>`
          : `<div class="empty">Henüz araştırma notu yok.</div>`
      }
    </div>
  `;
}

function researchEdit(note, isNew) {
  return `
    <div class="stack">
      <button class="btn btn-ghost" data-action="back">← Geri</button>
      <h2>${isNew ? "Yeni not" : "Notu düzenle"}</h2>
      <div class="field">
        <label>Bölüm</label>
        <select id="note-section">
          ${SECTIONS.map(
            ([id, label]) =>
              `<option value="${id}" ${note.section === id ? "selected" : ""}>${label}</option>`
          ).join("")}
        </select>
      </div>
      <div class="field">
        <label>Başlık</label>
        <input id="note-title" value="${escapeAttr(note.title || "")}" />
      </div>
      <div class="field">
        <label>İçerik</label>
        <textarea id="note-content">${escapeHtml(note.content || "")}</textarea>
      </div>
      <div class="row">
        <button class="btn btn-accent" data-action="save-research">${isNew ? "Ekle" : "Kaydet"}</button>
        ${!isNew ? `<button class="btn btn-danger" data-action="delete-research">Sil</button>` : ""}
      </div>
    </div>
  `;
}

function notesList() {
  return `
    <div class="stack">
      <div class="row" style="justify-content:space-between">
        <strong>Paper notes</strong>
        <button class="btn btn-primary" data-action="new-note">+ Not</button>
      </div>
      ${
        state.notes.length
          ? `<div class="list">${state.notes
              .map((n, i) => {
                const tag = (n.tags && n.tags[0]) || "genel";
                return `
                  <button class="item" data-open-note="${i}">
                    <div class="meta"><span class="badge">${escapeHtml(tag)}</span><span>${escapeHtml((n.updated_at || "").slice(0, 10))}</span></div>
                    <h3>${escapeHtml(n.title || "Untitled")}</h3>
                    ${n.paper_title ? `<p class="muted" style="margin:4px 0 0">${escapeHtml(truncate(n.paper_title, 80))}</p>` : ""}
                  </button>`;
              })
              .join("")}</div>`
          : `<div class="empty">Henüz paper note yok.</div>`
      }
    </div>
  `;
}

function noteEdit(entry, isNew) {
  return `
    <div class="stack">
      <button class="btn btn-ghost" data-action="back">← Geri</button>
      <h2>${isNew ? "Yeni paper note" : "Note düzenle"}</h2>
      <div class="field">
        <label>Başlık</label>
        <input id="pnote-title" value="${escapeAttr(entry.title || "")}" />
      </div>
      <div class="field">
        <label>Paper title</label>
        <input id="pnote-paper" value="${escapeAttr(entry.paper_title || "")}" />
      </div>
      <div class="field">
        <label>Tags (virgülle)</label>
        <input id="pnote-tags" value="${escapeAttr((entry.tags || []).join(", "))}" />
      </div>
      <div class="field">
        <label>İçerik</label>
        <textarea id="pnote-content">${escapeHtml(entry.content || "")}</textarea>
      </div>
      <div class="row">
        <button class="btn btn-accent" data-action="save-note">${isNew ? "Ekle" : "Kaydet"}</button>
        ${!isNew ? `<button class="btn btn-danger" data-action="delete-note">Sil</button>` : ""}
      </div>
    </div>
  `;
}

function summaryView() {
  return `
    <div class="stack">
      <button class="btn btn-ghost" data-action="back-detail">← Makale</button>
      <h2>Kayıtlı özet</h2>
      <div class="card prose">${escapeHtml(state.summaryText || "Özet bulunamadı.")}</div>
    </div>
  `;
}

function render() {
  if (!isConfigured()) {
    app.innerHTML = setupScreen();
    return;
  }

  const account = getAccount();
  if (!account) {
    app.innerHTML = loginScreen();
    bind();
    return;
  }

  let body = "";
  if (state.view === "summary") {
    body = summaryView();
  } else if (state.tab === "library") {
    body =
      state.view === "detail" && state.selected
        ? libraryDetail(state.selected)
        : libraryList();
  } else if (state.tab === "research") {
    body =
      state.view === "edit"
        ? researchEdit(state.selected || blankResearch(), !state.selected?.id)
        : researchList();
  } else {
    body =
      state.view === "edit"
        ? noteEdit(state.selected || blankNote(), !state.selected?.id)
        : notesList();
  }

  app.innerHTML = `
    ${topbar(state.busy ? "Yükleniyor…" : account.username || "")}
    <main class="screen">
      ${state.error ? `<div class="error-box">${escapeHtml(state.error)}</div>` : ""}
      ${body}
    </main>
    ${state.view === "list" ? nav() : ""}
    ${state.toast ? `<div class="toast">${escapeHtml(state.toast)}</div>` : ""}
  `;
  bind();
}

function blankResearch() {
  return { id: "", section: "ideas", title: "", content: "", created_at: "", updated_at: "" };
}

function blankNote() {
  return {
    id: "",
    entry_type: "note",
    title: "",
    paper_title: "",
    content: "",
    tags: ["genel"],
    created_at: "",
    updated_at: "",
  };
}

function bind() {
  app.querySelectorAll("[data-action]").forEach((el) => {
    el.addEventListener("click", onAction);
  });
  app.querySelectorAll("[data-tab]").forEach((el) => {
    el.addEventListener("click", () => {
      state.tab = el.getAttribute("data-tab");
      state.view = "list";
      state.selected = null;
      render();
    });
  });
  app.querySelectorAll("[data-open-library]").forEach((el) => {
    el.addEventListener("click", () => {
      const i = Number(el.getAttribute("data-open-library"));
      state.selected = state.library[i];
      state.view = "detail";
      render();
    });
  });
  app.querySelectorAll("[data-open-research]").forEach((el) => {
    el.addEventListener("click", () => {
      const i = Number(el.getAttribute("data-open-research"));
      state.selected = { ...state.research[i] };
      state.view = "edit";
      render();
    });
  });
  app.querySelectorAll("[data-open-note]").forEach((el) => {
    el.addEventListener("click", () => {
      const i = Number(el.getAttribute("data-open-note"));
      state.selected = { ...state.notes[i] };
      state.view = "edit";
      render();
    });
  });
}

async function onAction(ev) {
  const action = ev.currentTarget.getAttribute("data-action");
  if (action === "login") {
    await login();
    return;
  }
  if (action === "reload") {
    await withBusy(refreshAll);
    return;
  }
  if (action === "logout") {
    await logout();
    return;
  }
  if (action === "back") {
    state.view = "list";
    state.selected = null;
    render();
    return;
  }
  if (action === "back-detail") {
    state.view = "detail";
    state.summaryText = "";
    render();
    return;
  }
  if (action === "new-research") {
    state.selected = blankResearch();
    state.view = "edit";
    render();
    return;
  }
  if (action === "new-note") {
    state.selected = blankNote();
    state.view = "edit";
    render();
    return;
  }
  if (action === "save-quick-note") {
    const text = document.getElementById("quick-note")?.value || "";
    await withBusy(async () => {
      const papers = await loadLibrary();
      const target = papers.find((p) => p.openalex_id === state.selected.openalex_id);
      if (!target) throw new Error("Makale library’de bulunamadı (senkron?).");
      target.quick_note = text.trim();
      await saveLibrary(papers);
      state.library = papers;
      state.selected = { ...target };
      toast("Not kaydedildi");
    });
    return;
  }
  if (action === "open-summary") {
    await withBusy(async () => {
      const rel = toPaperReaderRelative(state.selected.summary_folder, config.paperReaderFolder);
      const path = ozetPath(rel);
      if (!path) throw new Error("Özet klasör yolu çözülemedi.");
      const text = await readTextFile(path);
      if (!text) throw new Error(`ozet.md bulunamadı: ${path}`);
      state.summaryText = text;
      state.view = "summary";
    });
    return;
  }
  if (action === "save-research") {
    const section = document.getElementById("note-section")?.value || "general";
    const title = document.getElementById("note-title")?.value || "";
    const content = document.getElementById("note-content")?.value || "";
    await withBusy(async () => {
      const notes = await loadResearchNotes();
      const stamp = nowIso();
      if (!state.selected?.id) {
        const note = {
          id: uid(),
          section,
          title: title.trim() || content.trim().split("\n")[0]?.slice(0, 72) || "Yeni not",
          content: content.trim(),
          created_at: stamp,
          updated_at: stamp,
        };
        notes.unshift(note);
      } else {
        const idx = notes.findIndex((n) => n.id === state.selected.id);
        const updated = {
          ...(idx >= 0 ? notes[idx] : state.selected),
          section,
          title: title.trim(),
          content: content.trim(),
          updated_at: stamp,
        };
        if (idx >= 0) notes[idx] = updated;
        else notes.unshift(updated);
      }
      await saveResearchNotes(notes);
      state.research = notes;
      state.view = "list";
      state.selected = null;
      toast("Research notu kaydedildi");
    });
    return;
  }
  if (action === "delete-research") {
    if (!confirm("Bu not silinsin mi?")) return;
    await withBusy(async () => {
      const notes = (await loadResearchNotes()).filter((n) => n.id !== state.selected.id);
      await saveResearchNotes(notes);
      state.research = notes;
      state.view = "list";
      state.selected = null;
      toast("Silindi");
    });
    return;
  }
  if (action === "save-note") {
    const title = document.getElementById("pnote-title")?.value || "";
    const paper = document.getElementById("pnote-paper")?.value || "";
    const content = document.getElementById("pnote-content")?.value || "";
    const tags = (document.getElementById("pnote-tags")?.value || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    await withBusy(async () => {
      const entries = await loadPaperNotebook();
      const stamp = nowIso();
      if (!state.selected?.id) {
        entries.unshift({
          id: uid(),
          entry_type: "note",
          title: title.trim() || content.trim().split("\n")[0]?.slice(0, 72) || "Yeni not",
          paper_title: paper.trim(),
          content: content.trim(),
          tags: tags.length ? tags : ["genel"],
          created_at: stamp,
          updated_at: stamp,
        });
      } else {
        const idx = entries.findIndex((n) => n.id === state.selected.id);
        const updated = {
          ...(idx >= 0 ? entries[idx] : state.selected),
          title: title.trim(),
          paper_title: paper.trim(),
          content: content.trim(),
          tags: tags.length ? tags : ["genel"],
          updated_at: stamp,
        };
        if (idx >= 0) entries[idx] = updated;
        else entries.unshift(updated);
      }
      await savePaperNotebook(entries);
      state.notes = entries;
      state.view = "list";
      state.selected = null;
      toast("Note kaydedildi");
    });
    return;
  }
  if (action === "delete-note") {
    if (!confirm("Bu note silinsin mi?")) return;
    await withBusy(async () => {
      const entries = (await loadPaperNotebook()).filter((n) => n.id !== state.selected.id);
      await savePaperNotebook(entries);
      state.notes = entries;
      state.view = "list";
      state.selected = null;
      toast("Silindi");
    });
  }
}

function escapeHtml(s) {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escapeAttr(s) {
  return escapeHtml(s).replaceAll("`", "&#96;");
}

function truncate(s, n) {
  const t = String(s || "");
  return t.length > n ? t.slice(0, n - 1) + "…" : t;
}

async function boot() {
  if (!isConfigured()) {
    render();
    return;
  }
  await initAuth();
  state.showLocalButton = await localDataAvailable();
  render();
  if (getAccount()) {
    await withBusy(refreshAll);
  }
}

boot();
